import { createId, toIndustryLabel } from "./chatMessageHelpers";
import { OnboardingChatView } from "./OnboardingChatView";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { clearAnswersForStep, resolveBackStep, rewindMessages } from "./chatBackNavigation";
import { getChatQuestion, formatRevenueDisplay, ALL_INDUSTRY_OPTIONS, ALL_LOCATION_OPTIONS } from "./questionFlow";
import { applyParsedAnswer, parseOnboardingAnswer } from "./parseAnswer";
import { STEP_ACTIONS, STEP_COUNT, STEP_CRM, STEP_INDUSTRY, STEP_LOCATION, STEP_NAME, STEP_REVENUE, resolveIndustryInclude } from "../onboardingShared";
import useCrmPreferenceAnswer from "./useCrmPreferenceAnswer";
import useCrmAvailability from "./useCrmAvailability";
import { toggleCrmSelection } from "./crmStep";
import { NO_ACTIONS_ANSWER, NO_ACTIONS_LABEL, parseActionStepAnswer } from "./actionStep";
import { useChatVoice } from "./useChatVoice";
import { shouldParseRemotely, useRemoteAnswerParser } from "./useRemoteAnswerParser";
import { FEATURED_INDUSTRIES, FALLBACK_REMAINING_INDUSTRIES, OTHER_INDUSTRY } from "../constants";
import { suggestSearchName } from "../../../helper/suggestSearchName";
import { getLocationApi, getOperationModel } from "../../../api/searchApi";

/**
 * Chat onboarding — the only onboarding experience.
 */

export default function OnboardingChat({
  answers, onAnswersChange, onComplete, onSkip, completionPending = false,
}) {
  const [step, setStep] = useState(STEP_INDUSTRY);
  const [messages, setMessages] = useState([]);
  const [pendingSelection, setPendingSelection] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [industryOptions, setIndustryOptions] = useState(ALL_INDUSTRY_OPTIONS);
  const [locationOptions, setLocationOptions] = useState(ALL_LOCATION_OPTIONS);
  const [otherMode, setOtherMode] = useState(false);
  const crmEnabled = useCrmAvailability();

  const threadRef = useRef(null);
  const completionTimerRef = useRef(null);
  const seededRef = useRef(false);

  const question = getChatQuestion(step);
  const stepCount = STEP_COUNT - (crmEnabled ? 0 : 1);
  const progressLabel = `${Math.min(step + 1 - (!crmEnabled && step > STEP_CRM ? 1 : 0), stepCount)} of ${stepCount}`;
  const hasConversation = messages.some((message) => message.role === "user");

  const effectiveIndustries = useMemo(
    () =>
      resolveIndustryInclude(
        answers.industryInclude,
        answers.otherSelected,
        answers.otherIndustries
      ),
    [answers.industryInclude, answers.otherIndustries, answers.otherSelected]
  );

  const nameSuggestion = useMemo(
    () =>
      suggestSearchName({
        industryInclude: effectiveIndustries,
        locations: answers.locations,
      }),
    [answers.locations, effectiveIndustries]
  );

  const remainingIndustries = useMemo(
    () => industryOptions.filter((label) => !FEATURED_INDUSTRIES.includes(label)),
    [industryOptions]
  );

  const activeSuggestions = useMemo(() => {
    if (step === STEP_INDUSTRY) {
      if (otherMode) {
        return remainingIndustries;
      }
      return [...FEATURED_INDUSTRIES, OTHER_INDUSTRY];
    }
    if (step === STEP_LOCATION) {
      return locationOptions.length > 0 ? locationOptions : ALL_LOCATION_OPTIONS;
    }
    if (step === STEP_REVENUE || step === STEP_ACTIONS || step === STEP_CRM) {
      return question.suggestions;
    }
    if (step === STEP_NAME) {
      return [{ label: nameSuggestion, value: { searchName: nameSuggestion } }];
    }
    return [];
  }, [
    locationOptions,
    nameSuggestion,
    otherMode,
    question.suggestions,
    remainingIndustries,
    step,
  ]);

  const { listening, stopVoice, handleToggleVoice } = useChatVoice({
    activeSuggestions, nameSuggestion, setDraft, step,
  });

  const scrollToBottom = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      return;
    }
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (!hasConversation) return;
    scrollToBottom();
  }, [hasConversation, messages, scrollToBottom, pendingSelection, listening]);

  // Load option catalogs (fall back when unauthenticated).
  useEffect(() => {
    let cancelled = false;
    getOperationModel()
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 200 && Array.isArray(res.data) && res.data.length > 0) {
          const labels = res.data.map(toIndustryLabel).filter(Boolean);
          const featuredSet = new Set(FEATURED_INDUSTRIES);
          const remaining = labels.filter((label) => !featuredSet.has(label));
          setIndustryOptions([
            ...FEATURED_INDUSTRIES,
            ...(remaining.length ? remaining : FALLBACK_REMAINING_INDUSTRIES),
          ]);
        }
      })
      .catch(() => {});
    getLocationApi()
      .then((res) => {
        if (cancelled) return;
        if (res?.status === 200 && Array.isArray(res.data) && res.data.length > 0) {
          setLocationOptions(res.data);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Seed the first app question once.
  useEffect(() => {
    if (seededRef.current) return;
    seededRef.current = true;
    setMessages([
      {
        id: createId("app"),
        role: "app",
        kind: "question",
        step: STEP_INDUSTRY,
        text: getChatQuestion(STEP_INDUSTRY).prompt,
      },
    ]);
  }, []);

  useEffect(
    () => () => {
      if (completionTimerRef.current) window.clearTimeout(completionTimerRef.current);
    },
    []
  );

  const appendMessages = useCallback((next) => {
    setMessages((current) => [...current, ...next]);
  }, []);

  const askNextQuestion = useCallback(
    (nextStep) => {
      const nextQuestion = getChatQuestion(nextStep);
      appendMessages([
        {
          id: createId("app"),
          role: "app",
          kind: "question",
          step: nextStep,
          text: nextQuestion.prompt,
        },
      ]);
      setStep(nextStep);
      setPendingSelection([]);
      setDraft("");
      setOtherMode(false);
      setBusy(false);
    },
    [appendMessages]
  );

  const commitAnswer = useCallback(
    (parsed, displayText) => {
      const nextAnswers = applyParsedAnswer(answers, step, parsed);
      onAnswersChange(nextAnswers);

      appendMessages([
        {
          id: createId("user"),
          role: "user",
          kind: "answer",
          step,
          text: displayText || parsed.display,
        },
      ]);

      if (parsed.needsFollowUp === "other-industry") {
        setOtherMode(true);
        setBusy(true);
        window.setTimeout(() => {
          appendMessages([
            {
              id: createId("app"),
              role: "app",
              kind: "follow-up",
              step: STEP_INDUSTRY,
              text: "Which other industries should we include?",
            },
          ]);
          setBusy(false);
        }, 220);
        setPendingSelection([]);
        setDraft("");
        return;
      }

      setBusy(true);
      // Let the hero → dock motion settle before the next app question appears.
      completionTimerRef.current = window.setTimeout(() => {
        if (step >= STEP_NAME) {
          setBusy(false);
          onComplete(nextAnswers);
          return;
        }
        askNextQuestion(step === STEP_ACTIONS && !crmEnabled ? STEP_NAME : step + 1);
      }, 520);
    },
    [answers, appendMessages, askNextQuestion, crmEnabled, onAnswersChange, onComplete, step]
  );

  const handleClarify = useCallback(() => {
    appendMessages([
      {
        id: createId("app"),
        role: "app",
        kind: "clarification",
        step,
        text: otherMode
          ? "I didn’t recognize that industry. Pick one below or try a clearer name."
          : question.clarification,
      },
    ]);
  }, [appendMessages, otherMode, question.clarification, step]);

  const { isPending: isRemotePending, resolveAnswer } = useRemoteAnswerParser();
  const { submit: submitCrmAnswer, error: crmError } = useCrmPreferenceAnswer({ step, setBusy, commitAnswer, handleClarify });
  const handleSubmitText = useCallback(
    (rawText) => {
      if (busy || completionPending || isRemotePending()) return;
      const text = String(rawText || "").trim();
      if (step === STEP_CRM) {
        submitCrmAnswer(pendingSelection, text);
        return;
      }
      if (step === STEP_ACTIONS) {
        const parsed = parseActionStepAnswer(pendingSelection, text);
        if (parsed.ok) commitAnswer(parsed, parsed.display);
        else handleClarify();
        setPendingSelection([]);
        setDraft("");
        return;
      }

      const selectionMode = otherMode ? "multiple" : question.selection;
      const combined =
        selectionMode === "multiple" && pendingSelection.length > 0
          ? [pendingSelection.join(", "), text].filter(Boolean).join(", ")
          : text;

      if (!combined && step !== STEP_NAME) {
        handleClarify();
        return;
      }

      const parseStep = otherMode ? STEP_INDUSTRY : step;
      const localParsed = parseOnboardingAnswer(parseStep, combined || nameSuggestion, {
        industryOptions,
        locationOptions,
      });

      const settleParsed = (parsed, { shown = false, clarification = "" } = {}) => {
        if (!parsed.ok) {
          if (!shown) {
            appendMessages([
              { id: createId("user"), role: "user", kind: "answer", step, text: combined || text },
            ]);
          }
          if (clarification) {
            appendMessages([
              { id: createId("app"), role: "app", kind: "clarification", step, text: clarification },
            ]);
          } else {
            handleClarify();
          }
          setPendingSelection([]);
          setDraft("");
          return;
        }

        // When in Other follow-up, merge extras into existing featured selection.
        if (otherMode && parsed.ok) {
          const merged = {
            ...parsed,
            value: {
              industryInclude: answers.industryInclude || [],
              otherIndustries: parsed.value.otherIndustries?.length
                ? parsed.value.otherIndustries
                : parsed.value.industryInclude || [],
              otherSelected: true,
            },
            display: (parsed.value.otherIndustries?.length
              ? parsed.value.otherIndustries
              : parsed.value.industryInclude || []
            ).join(", "),
          };
          if (!merged.display) {
            appendMessages([
              { id: createId("user"), role: "user", kind: "answer", step, text: combined },
            ]);
            handleClarify();
            return;
          }
          setOtherMode(false);
          commitAnswer(merged, merged.display);
          setPendingSelection([]);
          setDraft("");
          return;
        }

        commitAnswer(parsed, parsed.display);
        setPendingSelection([]);
        setDraft("");
      };

      // A chip-only submit arrives as the joined chip labels; only typed text is remote.
      const typedText =
        selectionMode === "multiple" && text === pendingSelection.join(", ") ? "" : text;
      if (!shouldParseRemotely(parseStep, typedText, localParsed)) {
        settleParsed(localParsed);
        return;
      }

      // Typed text goes to the backend LLM parser (owner ruling 2026-10-09). The
      // user's message shows at once; the composer stays busy until it settles.
      const pendingId = createId("user");
      appendMessages([{ id: pendingId, role: "user", kind: "answer", step, text: combined }]);
      setBusy(true);
      resolveAnswer({ parseStep, text: typedText, chips: pendingSelection, localParsed }).then((result) => {
        if (!result) return;
        setBusy(false);
        // On success the committed display replaces the raw typed text.
        if (result.parsed.ok) setMessages((current) => current.filter((m) => m.id !== pendingId));
        settleParsed(result.parsed, { shown: true, clarification: result.clarification });
      });
    },
    [
      answers.industryInclude,
      appendMessages,
      busy,
      commitAnswer,
      completionPending,
      handleClarify,
      industryOptions,
      isRemotePending,
      locationOptions,
      nameSuggestion,
      otherMode,
      pendingSelection,
      question.selection,
      resolveAnswer,
      submitCrmAnswer,
      step,
    ]
  );

  const handleToggleSuggestion = useCallback(
    (suggestion) => {
      if (busy || completionPending) return;
      const label = typeof suggestion === "string" ? suggestion : suggestion?.label;
      if (step === STEP_CRM) {
        setPendingSelection((current) => toggleCrmSelection(current, label));
        return;
      }

      if (step === STEP_REVENUE && suggestion?.value) {
        const display = formatRevenueDisplay(
          suggestion.value.revenueLowerBound,
          suggestion.value.revenueUpperBound
        );
        commitAnswer(
          {
            ok: true,
            value: suggestion.value,
            display,
          },
          display
        );
        return;
      }

      if (step === STEP_NAME && suggestion?.value) {
        commitAnswer(
          {
            ok: true,
            value: suggestion.value,
            display: suggestion.value.searchName,
          },
          suggestion.value.searchName
        );
        return;
      }

      if (step === STEP_ACTIONS && label === NO_ACTIONS_LABEL) {
        commitAnswer(NO_ACTIONS_ANSWER, NO_ACTIONS_ANSWER.display);
        return;
      }

      if (label === OTHER_INDUSTRY && !otherMode) {
        handleSubmitText(OTHER_INDUSTRY);
        return;
      }

      const selectionMode = otherMode ? "multiple" : question.selection;
      if (selectionMode === "multiple") {
        setPendingSelection((current) =>
          current.includes(label)
            ? current.filter((item) => item !== label)
            : [...current, label]
        );
        return;
      }

      setDraft(label);
    },
    [busy, commitAnswer, completionPending, handleSubmitText, otherMode, question.selection, step]
  );

  /** Step back to the previous question, rewinding the transcript with it. */
  const handleBack = useCallback(() => {
    // `busy` also covers the setTimeout in commitAnswer that advances the step,
    // so Back can never race a pending advance.
    if (busy || completionPending) return;

    const previousStep = resolveBackStep(step, otherMode);
    const targetStep = previousStep === STEP_CRM && !crmEnabled ? STEP_ACTIONS : previousStep;
    stopVoice();
    setMessages((current) => rewindMessages(current, targetStep));
    onAnswersChange(clearAnswersForStep(answers, targetStep));
    setStep(targetStep);
    // Reset exactly what askNextQuestion resets on the forward path.
    setPendingSelection([]);
    setDraft("");
    setOtherMode(false);
    setBusy(false);
  }, [answers, busy, completionPending, crmEnabled, onAnswersChange, otherMode, step, stopVoice]);

  const latestAppId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "app") return messages[i].id;
    }
    return null;
  }, [messages]);

  const openingQuestion =
    messages.find((message) => message.role === "app")?.text || question.prompt;

  return <OnboardingChatView {...{
      hasConversation, busy, completionPending, handleBack,
      onSkip, progressLabel, threadRef, messages,
      latestAppId, openingQuestion, step, nameSuggestion,
      otherMode, question, activeSuggestions, pendingSelection,
      handleToggleSuggestion, handleSubmitText, listening, handleToggleVoice,
      draft, setDraft, crmError
    }} />;
}
